// @flow
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchSimilarItems } from '../api/similarItems';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getItemCardDatas } from './cardData';
import { getUserExcludedHouses } from './searchExclusions';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_SIMILAR_ITEMS_FAIL = 'la/domain/similarItems/LOAD_FAIL';
export const LOAD_SIMILAR_ITEMS_REQUEST = 'la/domain/similarItems/LOAD_REQUEST';
export const LOAD_SIMILAR_ITEMS_SUCCESS = 'la/domain/similarItems/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SIMILAR_ITEMS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_SIMILAR_ITEMS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SIMILAR_ITEMS_SUCCESS:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.meta.itemId]: action.payload.itemIDs,
                },
                loaded: {
                    ...state.loaded,
                    [action.meta.itemId]: action.meta.actionTime,
                },
                loading: difference(state.loading, [action.meta.itemId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.similarItems;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

// takes in a (state, id) and returns data
export const getSimilarItemIds = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

// takes in a (state, id) and returns load time
const getLoadTimeForSimilarItems = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

// takes in a (state, id) and returns if data is loading
const isSimilarItemsLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const getSimilarItems = (state: GlobalState, itemId: number, limit?: number) => {
    let similarItemIds = getSimilarItemIds(state, itemId);
    similarItemIds = similarItemIds.filter(id => id !== itemId);
    similarItemIds = similarItemIds.slice(0, limit);
    return getItemCardDatas(state, similarItemIds);
};

const shouldFetchSimilarItems = (state, itemId) => {
    if (!itemId) return false;
    const item = getSimilarItems(state, itemId);
    if (item.length) {
        const loaded = getLoadTimeForSimilarItems(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isSimilarItemsLoading(state, itemId);
    return !loading;
};

/* ACTION CREATORS */
const loadSimilarItems = similarItemInfo => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const searchExclusions = getUserExcludedHouses(state);

        dispatch({
            payload: similarItemInfo.itemId,
            type: LOAD_SIMILAR_ITEMS_REQUEST,
        });

        const response = await fetchSimilarItems({ authToken, deployment, searchExclusions, similarItemInfo });
        dispatch({
            meta: { actionTime: Date.now(), itemId: similarItemInfo.itemId },
            payload: response.payload,
            type: LOAD_SIMILAR_ITEMS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemId: similarItemInfo.itemId },
            payload: error,
            type: LOAD_SIMILAR_ITEMS_FAIL,
        });
    }
};

export const fetchSimilarItemsIfNeeded = (similarItemInfo: any) => async (dispatch: Function, getState: Function) => {
    if (similarItemInfo && shouldFetchSimilarItems(getState(), similarItemInfo.itemId)) {
        return dispatch(loadSimilarItems(similarItemInfo));
    }
    return Promise.resolve();
};
